import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

function Methodology() {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) {
      return;
    }

    const target = document.getElementById(location.hash.slice(1));
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash]);

  return (
    <div className="methodology-page authenticated-page">
      <div className="methodology-container authenticated-page-content">
        <div className="methodology-hero">
          
          <h1 className="methodology-title">Methodology</h1>
          <p className="methodology-subtitle">
            How Black Peak Analytics' Investor Indicator ranking model works
          </p>
        </div>

        <section className="methodology-section">
          <h2 className="methodology-section-title">Overview</h2>
          <p>
            Black Peak Analytics uses a multi-factor scoring model called the <strong>Investor Indicator</strong> to rank stocks 
            based on fundamental and technical metrics. The model combines six component scores into a single 
            <strong> Investment Score</strong> that helps identify stocks that may be undervalued relative to their 
            quality, profitability, and historical performance.
          </p>
          <p>
            This is a <strong>research support tool</strong>, not a prediction engine. The rankings help you identify 
            stocks worth deeper investigation. They do not predict future stock prices, guarantee returns, or constitute 
            financial advice. Historical performance does not guarantee future results.
          </p>
        </section>

        <section className="methodology-section">
          <h2 className="methodology-section-title">Investment Score</h2>
          <p>
            The <strong>Investment Score</strong> is a composite metric ranging from 0 to 100 that combines six 
            underlying component scores:
          </p>
          <ul className="methodology-list">
            <li><strong>Valuation Score</strong> – How cheap the stock appears relative to earnings, book value, sales, and enterprise value</li>
            <li><strong>Profitability Score</strong> – How efficiently the company generates profit from revenue and equity</li>
            <li><strong>Balance Sheet Score</strong> – How strong the company's financial position and liquidity are</li>
            <li><strong>Growth and Cash Flow Score</strong> – How well the company is growing revenue and generating free cash flow</li>
            <li><strong>Risk Stability Score</strong> – How volatile the stock's price has been and how large its historical drawdowns were</li>
            <li><strong>Return Consistency Score</strong> – How reliably the stock has delivered positive returns over rolling time windows</li>
          </ul>
          <p>
            Each component score is calculated independently, then combined using a weighted average. Higher Investment 
            Scores indicate stocks that rank well across multiple dimensions. Stocks are ranked from highest to lowest 
            Investment Score to produce the final Top N list.
          </p>
        </section>

        <section className="methodology-section">
          <h2 className="methodology-section-title">Score Components</h2>
          <p>
            Each component score is calculated on a 0–100 scale. The model compares each stock with others in the selected universe, country, sector, and industry.
          </p>
        </section>

        <section className="methodology-component-card methodology-score-component-card">
          <h3 className="methodology-component-title">Valuation Score</h3>
          <p>
            The Valuation Score measures how cheap a stock appears based on traditional valuation ratios. The model 
            evaluates:
          </p>
          <ul className="methodology-list">
            <li><strong>Price-to-Earnings (P/E) Ratio</strong> – Lower is better; indicates cheaper earnings</li>
            <li><strong>Price-to-Book (P/B) Ratio</strong> – Lower is better; indicates cheaper book value</li>
            <li><strong>Price-to-Sales (P/S) Ratio</strong> – Lower is better; indicates cheaper revenue</li>
            <li><strong>Enterprise Value to EBITDA (EV/EBITDA)</strong> – Lower is better; indicates cheaper operating earnings</li>
            <li><strong>Enterprise Value to Gross Profit (EV/GP)</strong> – Lower is better; indicates cheaper gross profit</li>
          </ul>
          <p>
            Each metric is combined into a single score. Stocks with lower valuation ratios receive higher Valuation Scores.
          </p>
        </section>

        <section className="methodology-component-card methodology-score-component-card">
          <h3 className="methodology-component-title">Profitability Score</h3>
          <p>
            The Profitability Score measures how efficiently a company converts revenue and equity into profit. 
            The model evaluates:
          </p>
          <ul className="methodology-list">
            <li><strong>Return on Equity (ROE)</strong> – Higher is better; measures profit generated per dollar of shareholder equity</li>
            <li><strong>Operating Margin</strong> – Higher is better; measures operating profit as a percentage of revenue</li>
            <li><strong>Gross Profit</strong> – Higher is better; measures total gross profit in absolute terms</li>
            <li><strong>EBITDA</strong> – Higher is better; measures earnings before interest, taxes, depreciation, and amortization</li>
          </ul>
          <p>
            Each metric is combined into a single score. Companies with stronger profitability metrics receive higher Profitability Scores.
          </p>
        </section>

        <section className="methodology-component-card methodology-score-component-card">
          <h3 className="methodology-component-title">Balance Sheet Score</h3>
          <p>
            The Balance Sheet Score measures financial strength and liquidity. The model evaluates:
          </p>
          <ul className="methodology-list">
            <li><strong>Debt-to-Equity Ratio</strong> – Lower is better; indicates less leverage and lower financial risk</li>
            <li><strong>Current Ratio</strong> – Higher is better; measures ability to pay short-term obligations</li>
            <li><strong>Quick Ratio</strong> – Higher is better; measures ability to pay short-term obligations without selling inventory</li>
          </ul>
          <p>
            Each metric is combined into a single score. Companies with stronger balance sheets receive higher Balance Sheet Scores.
          </p>
        </section>

        <section className="methodology-component-card methodology-score-component-card">
          <h3 className="methodology-component-title">Growth and Cash Flow Score</h3>
          <p>
            The Growth and Cash Flow Score measures how well a company is growing and generating cash. The model evaluates:
          </p>
          <ul className="methodology-list">
            <li><strong>Revenue Growth</strong> – Higher is better; measures year-over-year revenue growth rate</li>
            <li><strong>Free Cash Flow</strong> – Higher is better; measures cash generated after capital expenditures</li>
          </ul>
          <p>
            Each metric is combined into a single score. Companies with stronger growth and cash generation receive higher scores.
          </p>
        </section>

        <section className="methodology-component-card methodology-score-component-card">
          <h3 className="methodology-component-title">Risk Stability Score</h3>
          <p>
            The Risk Stability Score measures how volatile and risky a stock's price behavior has been. The model evaluates:
          </p>
          <ul className="methodology-list">
            <li><strong>Maximum Drawdown</strong> – Lower is better; measures the largest peak-to-trough decline in price</li>
            <li><strong>Price Volatility</strong> – Lower is better; measures standard deviation of returns</li>
          </ul>
          <p>
            Each metric is combined into a single score. Stocks with lower volatility and smaller drawdowns receive 
            higher Risk Stability Scores.
          </p>
        </section>

        <section className="methodology-component-card methodology-score-component-card">
          <h3 className="methodology-component-title">Return Consistency Score</h3>
          <p>
            The Return Consistency Score measures how reliably a stock has delivered positive returns over rolling 
            time windows. The model evaluates:
          </p>
          <ul className="methodology-list">
            <li><strong>1-Year Rolling Return Win Rate</strong> – Percentage of 1-year periods that were positive</li>
            <li><strong>2-Year Rolling Return Win Rate</strong> – Percentage of 2-year periods that were positive</li>
            <li><strong>3-Year Rolling Return Win Rate</strong> – Percentage of 3-year periods that were positive</li>
          </ul>
          <p>
            Each metric is combined into a single score. Stocks with higher win rates across multiple time horizons receive higher Return 
            Consistency Scores.
          </p>
        </section>

        <section className="methodology-section">
          <h2 className="methodology-section-title">Historical Validation</h2>
          <p>
            The platform validates the Investor Indicator model using historical backtesting. At each historical 
            snapshot date, the model ranks stocks based on their Investment Scores at that time, then measures how 
            those ranked cohorts performed over completed forward-return windows.
          </p>
          <p>
            This process tests whether stocks that ranked highly under the model at historical points actually went 
            on to deliver stronger returns than lower-ranked stocks. The validation does not predict future performance, 
            but it provides evidence about whether the model's historical signals were associated with favorable outcomes.
          </p>
          <p>
            Key validation metrics include:
          </p>
          <ul className="methodology-list">
            <li><strong>Average Total Return</strong> – Mean return across all completed forward-return windows for a cohort</li>
            <li><strong>Win Rate</strong> – Percentage of completed forward-return windows that were positive</li>
            <li><strong>Matured Returns</strong> – Number of completed stock-level observations available for analysis</li>
          </ul>
        </section>

        <section className="methodology-section">
          <h2 className="methodology-section-title">S&P 500 index Benchmark Comparison</h2>
          <p>
            The platform compares the model's historical performance against the <strong>SPY ETF</strong> (S&P 500 
            index fund) as a passive benchmark. This comparison helps evaluate whether the model's active stock selection 
            historically outperformed a simple buy-and-hold S&P 500 strategy.
          </p>
          <p>
            The benchmark comparison uses S&P 500 index actual historical returns over the same time periods as the model's 
            cohort measurements. Outperformance is calculated as the difference between the model's compounded return 
            and S&P 500 index compounded return over the same validation window.
          </p>
          
        </section>

        <section className="methodology-section methodology-strategy-lab-section">
          <h2 className="methodology-section-title">Strategy Lab Methodology</h2>
          <h3 className="methodology-component-title">Turn stock rankings into a strategy you can evaluate.</h3>
          <p>Which highly ranked stocks should you hold? How many belong in your portfolio? How have different approaches performed over time?</p>
          <p><strong>Unlock Strategy Lab with Premium</strong> to explore these questions with historical evidence. Compare portfolio sizes, test annual rebalancing, and build a strategy plan using settings evaluated on later periods that were not used to select them.</p>
          <p>Explore the Investor Indicator ranking model across six universes: All Indexed Tickers (United States + Canada), S&amp;P 500, Nasdaq-100, Dow 30, S&amp;P/TSX 60, and S&amp;P/TSX Composite. Refine your analysis by country, sector, and industry.</p>
          <h3 className="methodology-component-title">Find the Right Balance Between Concentration and Diversification</h3>
          <p>Does a focused portfolio of top-ranked stocks outperform a broader selection historically? <strong>Cohort Performance Comparison</strong> lets you compare the evidence.</p>
          <p>At each historical snapshot, stocks matching your filters are ranked by Investment Score and grouped into Top 10, Top 25, Top 50, and Top 100 portfolios. Dow 30 uses Top 10, Top 20, and Top 30. Each group holds the same stocks throughout the selected forward-return window, without annual replacement.</p>
          <p>Compare completed <strong>1-, 2-, 3-, 4-, 6-, 8-, and 10-year results</strong>, including average return, median return, positive-result frequency, and the number of completed observations. Related fixed-cohort probability evidence also reports benchmark-win frequency for supported 1- through 4-year horizons when complete S&amp;P 500 index pairs are available.</p>
          <p><strong>See how portfolio size and holding period have shaped historical outcomes before choosing your approach.</strong></p>
          <h3 className="methodology-component-title">Compare Three Ways to Build Your Portfolio</h3>
          <p>A ranking is a starting point. How you allocate across those stocks also matters.</p>
          <p><strong>Annual Rebalanced Strategy Simulation</strong> builds a portfolio from the highest-ranked stocks, holds it until the next annual snapshot, then refreshes its holdings. Compare three allocation approaches:</p>
          <ul className="methodology-list">
            <li><strong>Balanced:</strong> Equal weighting across selected stocks.</li>
            <li><strong>Growth:</strong> Return Consistency³ weighting.</li>
            <li><strong>Conservative:</strong> Risk Stability³ weighting.</li>
          </ul>
          <p>Review positive periods, average annual portfolio return, compounded total return, matched S&amp;P 500 index returns, outperformance, and observed drawdown.</p>
          <p><strong>Explore the historical trade-offs between returns and declines across all three approaches.</strong> Strategy names describe their weighting methods; they do not guarantee growth or lower risk.</p>
          <h3 className="methodology-component-title">Discover Which Portfolio Sizes Worked Best Historically</h3>
          <p>Should you select the Top 10, Top 25, or a different number?</p>
          <p><strong>Historical Optimal Top N Finder</strong> tests every eligible portfolio size from Top 5 to your universe’s supported maximum, identifying the strongest full-sample compounded result for each strategy mode.</p>
          <p>Testing extends through Top 100 for All Indexed Tickers, S&amp;P 500, Nasdaq-100, and S&amp;P/TSX Composite; Top 60 for S&amp;P/TSX 60; and Top 30 for Dow 30.</p>
          <p><strong>Replace guesswork about portfolio size with a systematic historical comparison.</strong> Because the winner is selected using the full available history, it benefits from hindsight. It shows what worked best in that sample, not which setting will win in the future.</p>
          <h3 id="strategy-lab-walk-forward" className="methodology-component-title">Put Historical Winners to a More Demanding Test</h3>
          <p>A strategy can look compelling when its settings are chosen after seeing all the results. <strong>Walk-forward testing</strong> asks a more practical question: how did settings selected from earlier evidence perform in the next unseen period?</p>
          <p>For each test, Strategy Lab compares eligible Top N sizes using only earlier completed periods. It freezes the strongest training-period selection, applies it to the next unseen ranking period, and compares its performance with the S&amp;P 500 index.</p>
          <p>The training window then expands and the process repeats. Selected portfolio sizes may change as more evidence becomes available.</p>
          <p><strong>Build your strategy plan around decisions tested beyond the data used to choose them.</strong> When available, the current recommendation uses the latest prior-only walk-forward selection, rather than simply adopting the full-history winner.</p>
          <h3 className="methodology-component-title">Four Perspectives. A Better-Informed Decision.</h3>
          <p>Each report answers a different question:</p>
          <div className="validation-lab-table-wrapper">
            <table className="validation-lab-table">
              <thead><tr><th>Evidence</th><th>What it helps you assess</th></tr></thead>
              <tbody>
                <tr><td>Fixed-cohort comparison</td><td>How did the same selected stocks perform over different holding horizons?</td></tr>
                <tr><td>Annual rebalanced simulation</td><td>How did a strategy perform when its holdings were refreshed each year?</td></tr>
                <tr><td>Historical Optimal Top N</td><td>Which portfolio size delivered the strongest result across the full historical sample?</td></tr>
                <tr><td>Walk-forward testing</td><td>How did settings chosen using earlier periods perform in a later unseen period?</td></tr>
              </tbody>
            </table>
          </div>
          <p>Together, these views help you look beyond a single attractive return and assess the evidence behind a strategy.</p>
          <h3 className="methodology-component-title">Understand the Evidence Behind Every Result</h3>
          <p>Strategy Lab uses the <strong>S&amp;P 500 index as its benchmark across all supported universes</strong>, including Canadian universes. Strategy and benchmark returns are compared over matching completed periods. Outperformance is the difference between those returns.</p>
          <p>An observation is included only when its full forward horizon has completed and the required return data exists. Unavailable horizons appear as unavailable—not as zero returns.</p>
          <p><strong>Keep your strategy current with monthly alerts.</strong> Following alerts and removing stocks that fall outside your selected Top N can help prevent your live portfolio from drifting away from the latest rankings.</p>
          <h3 className="methodology-component-title">Make Your Next Strategy Decision With More Evidence</h3>
          <p><strong>Upgrade to Premium to unlock Strategy Lab</strong>—compare portfolio sizes, explore allocation approaches, and evaluate a walk-forward strategy plan before putting it into practice.</p>
        </section>

        <section className="methodology-section">
          <h2 className="methodology-section-title">Model Limitations</h2>
          <p>
            The Investor Indicator model has important limitations that users should understand:
          </p>
          <ul className="methodology-list">
            <li><strong>Not Financial Advice</strong> – Black Peak Analytics provides research tools, not personalized investment advice. Consult a qualified financial advisor before making investment decisions.</li>
             
            <li><strong>Market Regime Changes</strong> – The model's effectiveness may vary across different market regimes (bull markets, bear markets, high inflation, low inflation, etc.).</li>
          </ul>
          <p>
            Use the platform as one tool among many in your investment research process. Always perform your own 
            due diligence, understand the risks, and invest only what you can afford to lose.
          </p>
        </section>
        
      </div>
    </div>
  );
}

export default Methodology;
